/**
 * Firmware list API returns legacy `versions` (file names) plus `artifacts`
 * with semantic `version` from S3 object metadata (x-amz-meta-firmware-version).
 */

import type { FirmwareArtifactRow } from './types/HelperTypes';

export type { FirmwareArtifactRow };

function asRecord(data: unknown): Record<string, unknown> | null {
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
}

/** Normalize getFirmwareVersions / listFirmwareVersions IPC result. */
export function parseFirmwareListPayload(
  res: unknown,
  options?: { defaultRole?: string },
): {
  artifacts: FirmwareArtifactRow[];
  /** Sorted file basenames (legacy `versions` field — used for deprecate API & FW download command). */
  fileNames: string[];
} {
  const root = asRecord(res);
  const data = asRecord(root?.data) ?? root;

  const rawArtifacts = data?.artifacts;
  const artifacts: FirmwareArtifactRow[] = [];
  if (Array.isArray(rawArtifacts)) {
    for (const raw of rawArtifacts) {
      const row = asRecord(raw);
      if (!row) continue;
      const key = String(row.key ?? '').trim();
      const fileName = String(row.fileName ?? '').trim();
      const version = String(row.version ?? '').trim();
      if (!fileName) continue;
      artifacts.push({
        key,
        fileName,
        version: version || fileName,
      });
    }
  }

  const candidates: unknown[] =
    (Array.isArray(data?.versions) && data.versions) ||
    (Array.isArray(root?.versions) && root.versions) ||
    [];

  const fileNames: string[] = [];
  const seen = new Set<string>();
  for (const v of candidates) {
    const s = typeof v === 'string' ? v.trim() : '';
    if (!s || seen.has(s)) continue;
    seen.add(s);
    fileNames.push(s);
  }

  const role = String(options?.defaultRole ?? 'rear').replace(/\/+$/, '') || 'rear';

  if (artifacts.length === 0 && fileNames.length > 0) {
    for (const fn of fileNames) {
      artifacts.push({
        key: `${role}/${fn}`,
        fileName: fn,
        version: fn,
      });
    }
  } else {
    for (let i = 0; i < artifacts.length; i++) {
      const a = artifacts[i];
      if (!a.key && a.fileName) {
        artifacts[i] = { ...a, key: `${role}/${a.fileName}` };
      }
    }
  }

  return { artifacts, fileNames };
}
